import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';

/**
 * VenomHireButton — Smooth 120 FPS Venom Symbiote Web Button
 * 
 * Performance & Polish:
 *   - Zero React re-renders on mouse movement (direct DOM transforms & lerp physics).
 *   - Clean, smooth fluid wave harmonics without jitter or cursor rotation.
 *   - React Portal to document.body guarantees 100% pixel-perfect coordinates.
 *   - Magnetic button attraction with buttery spring damping.
 */
// Evaluate point on cubic bezier
function getCubicBezierPoint(p0, p1, p2, p3, t) {
  const mt = 1 - t;
  const mt2 = mt * mt;
  const mt3 = mt2 * mt;
  const t2 = t * t;
  const t3 = t2 * t;
  return {
    x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
    y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y,
  };
}

// Split cubic bezier from [0..t] using de Casteljau's algorithm
function getSubCubicBezier(p0, p1, p2, p3, t) {
  if (t >= 0.999) {
    return {
      cp1: p1,
      cp2: p2,
      end: p3,
      tangent: { x: p3.x - p2.x, y: p3.y - p2.y },
    };
  }
  const p01 = { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t };
  const p12 = { x: p1.x + (p2.x - p1.x) * t, y: p1.y + (p2.y - p1.y) * t };
  const p23 = { x: p2.x + (p3.x - p2.x) * t, y: p2.y + (p3.y - p2.y) * t };

  const p012 = { x: p01.x + (p12.x - p01.x) * t, y: p01.y + (p12.y - p01.y) * t };
  const p123 = { x: p12.x + (p23.x - p12.x) * t, y: p12.y + (p23.y - p12.y) * t };

  const p0123 = { x: p012.x + (p123.x - p012.x) * t, y: p012.y + (p123.y - p012.y) * t };

  return {
    cp1: p01,
    cp2: p012,
    end: p0123,
    tangent: { x: p123.x - p012.x, y: p123.y - p012.y },
  };
}

const VenomHireButton = ({
  to = '?section=contact',
  children = 'Hire Me',
  sx = {},
  onClick,
}) => {
  const theme = useTheme();
  const primaryColor = theme?.palette?.primary?.main || 'rgb(255, 78, 35)';
  const textColor = theme?.palette?.text?.primary || '#ffffff';
  const webColor = '#9ca3af'; // sleek spider/symbiote web gray
  const webGlowColor = 'rgba(156, 163, 175, 0.7)';
  const webCoreColor = '#1f2937';
  const webHighlightColor = '#e5e7eb';

  const buttonRef = useRef(null);
  const canvasRef = useRef(null);
  const mouseStateRef = useRef({
    x: -1000,
    y: -1000,
    cursorX: -1000,
    cursorY: -1000,
    isNear: false,
    pull: 0,
    smoothPull: 0,
    targetDragX: 0,
    targetDragY: 0,
    currentDragX: 0,
    currentDragY: 0,
  });

  const animFrameRef = useRef(null);
  const particlesRef = useRef([]);
  const ripplesRef = useRef([]);
  const tendrilsRef = useRef(
    Array.from({ length: 8 }, () => ({
      progress: 0,
      attached: false,
      recoil: 0,
    }))
  );
  const [mounted, setMounted] = useState(false);
  const isAttackingRef = useRef(false);
  const isTrappedRef = useRef(false);
  const snapCooldownRef = useRef(0);

  const ATTACK_RANGE = 145; // Distance to trigger attack
  const BREAK_DISTANCE = 165; // Snaps immediately as soon as cursor reaches stretch limit

  useEffect(() => {
    setMounted(true);
  }, []);

  // Window resize handler: update canvas resolution only on resize
  const handleResize = useCallback(() => {
    if (!canvasRef.current) return;
    canvasRef.current.width = window.innerWidth;
    canvasRef.current.height = window.innerHeight;
  }, []);

  // High-performance passive mouse tracking (ZERO React state updates)
  const handleMouseMove = useCallback((e) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();

    // If navbar is closed / hidden offscreen (e.g. scrolled away), cancel web immediately
    if (rect.bottom <= 5 || rect.top >= window.innerHeight) {
      if (isTrappedRef.current || mouseStateRef.current.isNear) {
        isTrappedRef.current = false;
        isAttackingRef.current = false;
        window.__venomCursorRestraint = { active: false, x: e.clientX, y: e.clientY, tension: 0 };
        window.dispatchEvent(new CustomEvent('venom-attack', { detail: { intensity: 0 } }));
        mouseStateRef.current.isNear = false;
        mouseStateRef.current.pull = 0;
        mouseStateRef.current.smoothPull = 0;
      }
      return;
    }

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const dx = e.clientX - centerX;
    const dy = e.clientY - centerY;
    const distance = Math.hypot(dx, dy) || 1;
    const nx = dx / distance;
    const ny = dy / distance;

    const now = Date.now();
    const state = mouseStateRef.current;

    state.x = e.clientX;
    state.y = e.clientY;
    if (state.cursorX < -500) {
      state.cursorX = e.clientX;
      state.cursorY = e.clientY;
    }

    // Check if web should attack and trap the custom cursor
    if (!isTrappedRef.current) {
      if (distance < ATTACK_RANGE && now > snapCooldownRef.current) {
        isTrappedRef.current = true;
        if (!isAttackingRef.current) {
          isAttackingRef.current = true;
          window.dispatchEvent(new CustomEvent('venom-attack', { detail: { intensity: 1 } }));
        }
      }
    }

    if (isTrappedRef.current) {
      // User is caught in the web!
      // Check if user has pulled beyond the elastic stretch limit
      if (distance > BREAK_DISTANCE) {
        // --- SNAP! Tendrils break IMMEDIATELY, zero lingering, instant return ---
        isTrappedRef.current = false;
        snapCooldownRef.current = now + 350;
        isAttackingRef.current = false;

        // Release cursor restraint IMMEDIATELY
        window.__venomCursorRestraint = { active: false, x: e.clientX, y: e.clientY, tension: 0 };
        window.dispatchEvent(new CustomEvent('venom-attack', { detail: { intensity: 0 } }));

        state.isNear = false;
        state.pull = 0;
        state.smoothPull = 0; // Cut off smoothPull immediately so web doesn't linger!
        state.targetDragX = 0;
        state.targetDragY = 0;

        // Destroy tendril progress immediately — no lingering on screen!
        tendrilsRef.current.forEach((t) => {
          t.progress = 0;
          t.attached = false;
          t.recoil = 0;
        });

        // Burst of snap splatter droplets
        const snapX = state.cursorX;
        const snapY = state.cursorY;
        for (let s = 0; s < 10; s++) {
          const sAngle = Math.random() * Math.PI * 2;
          const sSpeed = 2.5 + Math.random() * 4.5;
          particlesRef.current.push({
            x: snapX,
            y: snapY,
            vx: Math.cos(sAngle) * sSpeed,
            vy: Math.sin(sAngle) * sSpeed,
            life: 0.6,
            color: Math.random() > 0.4 ? webColor : '#ffffff',
            size: 2 + Math.random() * 2,
          });
        }
      } else {
        // --- WHILE LEAVING / ATTACHED: Custom cursor stays with the web! ---
        const relaxDist = 80;
        let restrainedDist = distance;
        let tension = 0;

        if (distance > relaxDist) {
          const stretch = distance - relaxDist;
          tension = Math.min(1, stretch / (BREAK_DISTANCE - relaxDist));
          // Clean elastic curve: stretches smoothly right up to the break point!
          restrainedDist = relaxDist + (stretch * 0.55);
        }

        const restrainedX = centerX + nx * restrainedDist;
        const restrainedY = centerY + ny * restrainedDist;

        // Tell custom cursor to stay firmly with the web
        window.__venomCursorRestraint = {
          active: true,
          x: restrainedX,
          y: restrainedY,
          tension,
        };

        // Button magnetic pull: button stretches toward cursor with tension
        const maxDrag = 40;
        const dragPull = 12 + tension * 24;
        state.targetDragX = nx * Math.min(dragPull, maxDrag);
        state.targetDragY = ny * Math.min(dragPull, maxDrag);
        state.pull = Math.max(0.4, 1 - tension * 0.3);
        state.isNear = true;
      }
    } else {
      // Normal state when not trapped
      if (state.isNear) {
        state.isNear = false;
        state.pull = 0;
        state.targetDragX = 0;
        state.targetDragY = 0;
        if (isAttackingRef.current) {
          isAttackingRef.current = false;
          window.dispatchEvent(new CustomEvent('venom-attack', { detail: { intensity: 0 } }));
        }
      }
      window.__venomCursorRestraint = { active: false, x: e.clientX, y: e.clientY, tension: 0 };
    }
  }, [ATTACK_RANGE, BREAK_DISTANCE]);

  // When scrolling or using mouse wheel (navbar hides), close and snap web immediately
  const handleScroll = useCallback(() => {
    if (isTrappedRef.current || mouseStateRef.current.isNear) {
      isTrappedRef.current = false;
      isAttackingRef.current = false;
      window.__venomCursorRestraint = { active: false, x: 0, y: 0, tension: 0 };
      window.dispatchEvent(new CustomEvent('venom-attack', { detail: { intensity: 0 } }));

      const state = mouseStateRef.current;
      state.isNear = false;
      state.pull = 0;
      state.smoothPull = 0;
      state.targetDragX = 0;
      state.targetDragY = 0;

      tendrilsRef.current.forEach((t) => {
        t.progress = 0;
        t.attached = false;
        t.recoil = 0;
      });

      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  }, []);

  // If cursor is attached to the web, clicking anywhere activates the button
  const handleClick = useCallback((e) => {
    if (isTrappedRef.current && buttonRef.current) {
      if (!buttonRef.current.contains(e.target)) {
        buttonRef.current.click();
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.dispatchEvent(new CustomEvent('venom-attack', { detail: { intensity: 0 } }));
      window.__venomCursorRestraint = { active: false };
    };
  }, [handleClick, handleMouseMove, handleResize, handleScroll]);

  // Smooth 120 FPS Symbiote Render Loop
  useEffect(() => {
    let time = 0;

    const render = () => {
      const canvas = canvasRef.current;
      const state = mouseStateRef.current;

      // Smooth lerp for pull factor and magnetic button drag (pure buttery physics)
      state.smoothPull += (state.pull - state.smoothPull) * 0.18;

      // Gentle, cushioned glide when returning to rest (0.08 lerp), responsive follow when pulling (0.18 lerp)
      const dragLerp = state.pull > 0.01 ? 0.18 : 0.08;
      state.currentDragX += (state.targetDragX - state.currentDragX) * dragLerp;
      state.currentDragY += (state.targetDragY - state.currentDragY) * dragLerp;

      // Synchronize exact position with the custom cursor ring (0.35 lerp)
      state.cursorX += (state.x - state.cursorX) * 0.35;
      state.cursorY += (state.y - state.cursorY) * 0.35;

      // Direct DOM style update for magnetic drag (bypasses React re-renders)
      if (buttonRef.current) {
        const dragDist = Math.hypot(state.currentDragX, state.currentDragY);
        if (dragDist > 0.15 || state.smoothPull > 0.01) {
          buttonRef.current.style.transform = `translate3d(${state.currentDragX.toFixed(2)}px, ${state.currentDragY.toFixed(2)}px, 0)`;
          buttonRef.current.style.boxShadow = `0 0 ${Math.round(22 * state.smoothPull)}px rgba(156, 163, 175, 0.4)`;
          buttonRef.current.style.borderColor = state.smoothPull > 0.4 ? webColor : primaryColor;
        } else {
          state.currentDragX = 0;
          state.currentDragY = 0;
          buttonRef.current.style.transform = 'translate3d(0, 0, 0)';
          buttonRef.current.style.boxShadow = 'none';
          buttonRef.current.style.borderColor = primaryColor;
        }
      }

      if (canvas) {
        // Guarantee canvas drawing buffer strictly matches window viewport
        if (canvas.width !== window.innerWidth || canvas.height !== window.innerHeight) {
          canvas.width = window.innerWidth;
          canvas.height = window.innerHeight;
        }

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const pull = state.smoothPull;
        const rect = buttonRef.current ? buttonRef.current.getBoundingClientRect() : null;
        const isButtonVisible = Boolean(rect && rect.bottom > 5 && rect.top < window.innerHeight);

        // If the navbar has closed (slid offscreen on scroll), immediately reset web and do not draw
        if (!isButtonVisible) {
          if (isTrappedRef.current) {
            isTrappedRef.current = false;
            window.__venomCursorRestraint = { active: false, x: 0, y: 0, tension: 0 };
            window.dispatchEvent(new CustomEvent('venom-attack', { detail: { intensity: 0 } }));
          }
          state.pull = 0;
          state.smoothPull = 0;
          tendrilsRef.current.forEach((t) => {
            t.progress = 0;
            t.attached = false;
            t.recoil = 0;
          });
          animFrameRef.current = requestAnimationFrame(render);
          return;
        }

        const restraint = window.__venomCursorRestraint;
        const isRestrained = Boolean(restraint && restraint.active);
        const cursorX = isRestrained
          ? (restraint.visualX || restraint.x)
          : state.cursorX;
        const cursorY = isRestrained
          ? (restraint.visualY || restraint.y)
          : state.cursorY;
        const tension = isRestrained ? (restraint.tension || 0) : 0;
        const cursorRadius = 15; // Exact radius of 30px custom cursor

        // Check if any tendril is currently extended or retracting
        let hasActiveTendril = false;
        for (let tIdx = 0; tIdx < 8; tIdx++) {
          if (tendrilsRef.current[tIdx].progress > 0.01) {
            hasActiveTendril = true;
            break;
          }
        }

        if ((pull > 0.01 || hasActiveTendril || isTrappedRef.current) && rect) {
          time += 0.045; // Smooth, rhythmic wave speed

          // 8 Directional anchor points around the button edge with 1-to-1 matching angles on the cursor ring
          const anchors = [
            { x: rect.left + rect.width * 0.12, y: rect.top, targetAngle: Math.PI * 1.25 }, // Top-Left -> Top-Left of cursor
            { x: rect.left + rect.width * 0.5, y: rect.top, targetAngle: Math.PI * 1.5 },   // Top-Mid -> Top-Mid of cursor
            { x: rect.left + rect.width * 0.88, y: rect.top, targetAngle: Math.PI * 1.75 }, // Top-Right -> Top-Right of cursor
            { x: rect.right, y: rect.top + rect.height * 0.5, targetAngle: 0 },             // Right -> Right of cursor
            { x: rect.left + rect.width * 0.88, y: rect.bottom, targetAngle: Math.PI * 0.25 }, // Bottom-Right -> Bottom-Right of cursor
            { x: rect.left + rect.width * 0.5, y: rect.bottom, targetAngle: Math.PI * 0.5 },  // Bottom-Mid -> Bottom-Mid of cursor
            { x: rect.left + rect.width * 0.12, y: rect.bottom, targetAngle: Math.PI * 0.75 }, // Bottom-Left -> Bottom-Left of cursor
            { x: rect.left, y: rect.top + rect.height * 0.5, targetAngle: Math.PI },          // Left -> Left of cursor
          ];

          // Compute distances to prioritize closest tendrils (organic predatory strike sequence)
          const distances = anchors.map((a) => Math.hypot(a.x - cursorX, a.y - cursorY));
          const minDistance = Math.min(...distances);

          const tendrilPoints = [];

          // 1. Draw smooth organic tendrils latching onto the cursor ring with 1-to-1 directional mapping
          anchors.forEach((anchor, i) => {
            const tendril = tendrilsRef.current[i];
            const ringAngle = anchor.targetAngle;
            const targetX = cursorX + Math.cos(ringAngle) * cursorRadius;
            const targetY = cursorY + Math.sin(ringAngle) * cursorRadius;

            // Staggered launch speed: closer tendrils launch first, farther follow rapidly
            const distDiff = distances[i] - minDistance;
            const delayOffset = Math.min(0.25, distDiff / 350);

            if (isTrappedRef.current || pull > 0.01) {
              // Rapid, aggressive whip strike speed
              const strikeSpeed = 0.09 + (1 - delayOffset) * 0.07;
              tendril.progress = Math.min(1, tendril.progress + strikeSpeed);
            } else {
              // Retract smoothly
              tendril.progress = Math.max(0, tendril.progress - 0.12);
              tendril.attached = false;
            }

            const p = tendril.progress;
            if (p <= 0.01) return;

            // Check for attachment impact event (strike lands on cursor ring)
            if (p >= 1 && !tendril.attached) {
              tendril.attached = true;
              tendril.recoil = 1.0; // Trigger elastic snap recoil

              // Spawn kinetic impact shockwave ring at attachment point
              ripplesRef.current.push({
                x: targetX,
                y: targetY,
                radius: 2,
                maxRadius: 18,
                life: 1.0,
                color: webColor,
              });

              // Spawn impact splatter droplets
              for (let s = 0; s < 4; s++) {
                const sAngle = ringAngle + (Math.random() - 0.5) * 1.2;
                const sSpeed = 2 + Math.random() * 3.5;
                particlesRef.current.push({
                  x: targetX,
                  y: targetY,
                  vx: Math.cos(sAngle) * sSpeed,
                  vy: Math.sin(sAngle) * sSpeed,
                  life: 0.8,
                  color: Math.random() > 0.3 ? webColor : webHighlightColor,
                  size: 2.2 + Math.random() * 2,
                });
              }
            }

            // Tendril recoil vibration upon locking
            let recoilWave = 0;
            if (tendril.recoil > 0.01) {
              recoilWave = Math.sin(time * 24 + i * 2) * 8 * tendril.recoil;
              tendril.recoil *= 0.86; // Snappy exponential damping
            }

            const dx = targetX - anchor.x;
            const dy = targetY - anchor.y;
            const len = Math.hypot(dx, dy) || 1;
            const nx = -dy / len;
            const ny = dx / len;

            // Fluid sinusoidal waves + recoil twang + tension stiffness
            const waveFreq = 4.2 + (i % 3) * 0.4 + tension * 8;
            const waveAmp = ((12 + Math.sin(time * 3 + i * 1.5) * 5) * (1 - tension * 0.35)) * Math.min(1, p * 1.2) + recoilWave;

            const p0 = { x: anchor.x, y: anchor.y };
            const p1 = {
              x: anchor.x + dx * 0.32 + nx * Math.sin(time * waveFreq + i) * waveAmp,
              y: anchor.y + dy * 0.32 + ny * Math.sin(time * waveFreq + i) * waveAmp,
            };
            const p2 = {
              x: anchor.x + dx * 0.72 + nx * Math.cos(time * (waveFreq + 0.8) + i * 1.6) * (waveAmp * 0.7),
              y: anchor.y + dy * 0.72 + ny * Math.cos(time * (waveFreq + 0.8) + i * 1.6) * (waveAmp * 0.7),
            };
            const p3 = { x: targetX, y: targetY };

            // Dynamic Sub-curve based on shooting extension progress
            const sub = getSubCubicBezier(p0, p1, p2, p3, p);

            tendrilPoints.push({
              index: i,
              progress: p,
              attached: tendril.attached,
              cp1x: sub.cp1.x,
              cp1y: sub.cp1.y,
              cp2x: sub.cp2.x,
              cp2y: sub.cp2.y,
              tipX: sub.end.x,
              tipY: sub.end.y,
              targetX,
              targetY,
            });

            // Glowing Outer Sheen (thins tautly under tension) — sleek gray symbiote web
            ctx.beginPath();
            ctx.moveTo(anchor.x, anchor.y);
            ctx.bezierCurveTo(sub.cp1.x, sub.cp1.y, sub.cp2.x, sub.cp2.y, sub.end.x, sub.end.y);
            ctx.strokeStyle = webColor;
            ctx.lineWidth = Math.max(1.2, 3.4 * (p * 0.7 + pull * 0.3) * (1 - tension * 0.25));
            ctx.shadowColor = webGlowColor;
            ctx.shadowBlur = (10 + tension * 4) * pull;
            ctx.stroke();

            // Inner Dark Symbiote Core
            ctx.beginPath();
            ctx.moveTo(anchor.x, anchor.y);
            ctx.bezierCurveTo(sub.cp1.x, sub.cp1.y, sub.cp2.x, sub.cp2.y, sub.end.x, sub.end.y);
            ctx.strokeStyle = webCoreColor;
            ctx.lineWidth = Math.max(0.6, 1.7 * (p * 0.7 + pull * 0.3) * (1 - tension * 0.25));
            ctx.shadowBlur = 0;
            ctx.stroke();

            // In-flight striking spearhead barb (before contact)
            if (p < 0.99) {
              const tLen = Math.hypot(sub.tangent.x, sub.tangent.y) || 1;
              const tx = sub.tangent.x / tLen;
              const ty = sub.tangent.y / tLen;
              const normX = -ty;
              const normY = tx;

              // Striking arrowhead barb
              ctx.beginPath();
              ctx.moveTo(sub.end.x + tx * 8, sub.end.y + ty * 8);
              ctx.lineTo(sub.end.x - tx * 4 + normX * 4, sub.end.y - ty * 4 + normY * 4);
              ctx.lineTo(sub.end.x - tx * 2, sub.end.y - ty * 2);
              ctx.lineTo(sub.end.x - tx * 4 - normX * 4, sub.end.y - ty * 4 - normY * 4);
              ctx.closePath();
              ctx.fillStyle = webColor;
              ctx.shadowColor = webGlowColor;
              ctx.shadowBlur = 8;
              ctx.fill();

              // Trailing in-flight droplets
              if (Math.random() < 0.35) {
                particlesRef.current.push({
                  x: sub.end.x - tx * 3,
                  y: sub.end.y - ty * 3,
                  vx: -tx * 1.5 + (Math.random() - 0.5),
                  vy: -ty * 1.5 + (Math.random() - 0.5),
                  life: 0.6,
                  color: webColor,
                  size: 1.5 + Math.random() * 1.5,
                });
              }
            } else {
              // Locked Clamping Claw & Talons (Attached to Cursor Ring)
              const clawSpread = 0.26;
              ctx.beginPath();
              ctx.arc(cursorX, cursorY, cursorRadius + 1.2, ringAngle - clawSpread, ringAngle + clawSpread);
              ctx.strokeStyle = webColor;
              ctx.lineWidth = Math.max(2.2, 3.2 * pull);
              ctx.stroke();

              // Inward biting talons
              const t1x = cursorX + Math.cos(ringAngle - clawSpread) * (cursorRadius - 1.5);
              const t1y = cursorY + Math.sin(ringAngle - clawSpread) * (cursorRadius - 1.5);
              const t2x = cursorX + Math.cos(ringAngle + clawSpread) * (cursorRadius - 1.5);
              const t2y = cursorY + Math.sin(ringAngle + clawSpread) * (cursorRadius - 1.5);

              ctx.beginPath();
              ctx.moveTo(cursorX + Math.cos(ringAngle - clawSpread) * cursorRadius, cursorY + Math.sin(ringAngle - clawSpread) * cursorRadius);
              ctx.lineTo(t1x, t1y);
              ctx.moveTo(cursorX + Math.cos(ringAngle + clawSpread) * cursorRadius, cursorY + Math.sin(ringAngle + clawSpread) * cursorRadius);
              ctx.lineTo(t2x, t2y);
              ctx.strokeStyle = webHighlightColor;
              ctx.lineWidth = 1.3;
              ctx.stroke();

              // Traveling organic pulse node rippling along the tendril
              const pulseT = ((time * 1.6 + i * 0.28) % 1);
              const pulsePt = getCubicBezierPoint(p0, p1, p2, p3, pulseT);
              ctx.beginPath();
              ctx.arc(pulsePt.x, pulsePt.y, 1.8, 0, Math.PI * 2);
              ctx.fillStyle = '#ffffff';
              ctx.shadowColor = webColor;
              ctx.shadowBlur = 5;
              ctx.fill();
            }

            // Anchor bead on button
            ctx.beginPath();
            ctx.arc(anchor.x, anchor.y, Math.max(1.8, 3.2 * (p * 0.7 + pull * 0.3)), 0, Math.PI * 2);
            ctx.fillStyle = webColor;
            ctx.fill();
          });

          // 2. Interconnecting Web Threads (Form as tendrils approach / attach)
          ctx.strokeStyle = webColor;
          ctx.shadowColor = webGlowColor;

          for (let i = 0; i < tendrilPoints.length; i++) {
            const nextIdx = (i + 1) % tendrilPoints.length;
            const curr = tendrilPoints[i];
            const next = tendrilPoints[nextIdx];

            // Only form interconnecting web when both neighboring tendrils are extending
            const webProgress = Math.min(curr.progress, next.progress);
            if (webProgress > 0.45) {
              const webAlpha = Math.min(1, (webProgress - 0.45) * 2) * pull;
              ctx.lineWidth = Math.max(0.6, 1.2 * webAlpha);
              ctx.shadowBlur = 5 * webAlpha;

              // Web strand 1
              ctx.beginPath();
              ctx.moveTo(curr.cp1x, curr.cp1y);
              const midX1 = (curr.cp1x + next.cp1x) / 2 + Math.sin(time * 4 + i) * 3;
              const midY1 = (curr.cp1y + next.cp1y) / 2 + Math.cos(time * 4 + i) * 3;
              ctx.quadraticCurveTo(midX1, midY1, next.cp1x, next.cp1y);
              ctx.stroke();

              // Web strand 2 (closer to cursor)
              if (webProgress > 0.75) {
                ctx.beginPath();
                ctx.moveTo(curr.cp2x, curr.cp2y);
                const midX2 = (curr.cp2x + next.cp2x) / 2 + Math.cos(time * 5 + i) * 2;
                const midY2 = (curr.cp2y + next.cp2y) / 2 + Math.sin(time * 5 + i) * 2;
                ctx.quadraticCurveTo(midX2, midY2, next.cp2x, next.cp2y);
                ctx.stroke();
              }
            }
          }

          // 3. Impact Shockwave Rings (Expanding Kinetic Bursts at Attachment Points)
          for (let r = ripplesRef.current.length - 1; r >= 0; r--) {
            const rip = ripplesRef.current[r];
            rip.radius += 1.3;
            rip.life -= 0.055;
            if (rip.life <= 0) {
              ripplesRef.current.splice(r, 1);
              continue;
            }
            ctx.beginPath();
            ctx.arc(rip.x, rip.y, rip.radius, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(156, 163, 175, ${rip.life.toFixed(2)})`;
            ctx.lineWidth = Math.max(1, 2.5 * rip.life);
            ctx.shadowColor = rip.color;
            ctx.shadowBlur = 8 * rip.life;
            ctx.stroke();
          }

          // 4. Smooth symbiote energy sparks (capped particle pool)
          if (pull > 0.5 && Math.random() < 0.4 && particlesRef.current.length < 32) {
            const pAngle = Math.random() * Math.PI * 2;
            const pSpeed = 1.5 + Math.random() * 2.5;
            particlesRef.current.push({
              x: cursorX + Math.cos(pAngle) * cursorRadius,
              y: cursorY + Math.sin(pAngle) * cursorRadius,
              vx: Math.cos(pAngle) * pSpeed,
              vy: Math.sin(pAngle) * pSpeed,
              life: 1.0,
              color: Math.random() > 0.35 ? webColor : webHighlightColor,
              size: 2 + Math.random() * 2,
            });
          }

          for (let i = particlesRef.current.length - 1; i >= 0; i--) {
            const p = particlesRef.current[i];
            p.x += p.vx;
            p.y += p.vy;
            p.life -= 0.045;

            if (p.life <= 0) {
              particlesRef.current.splice(i, 1);
              continue;
            }

            ctx.beginPath();
            ctx.arc(p.x, p.y, Math.max(0.5, p.size * p.life), 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.shadowColor = p.color;
            ctx.shadowBlur = 6;
            ctx.fill();
          }

          // 5. Subtle glowing aura ring around cursor
          ctx.beginPath();
          ctx.arc(cursorX, cursorY, cursorRadius + 2.5, 0, Math.PI * 2);
          ctx.strokeStyle = webColor;
          ctx.lineWidth = Math.max(0.7, 1.3 * pull);
          ctx.shadowColor = webGlowColor;
          ctx.shadowBlur = 8 * pull;
          ctx.stroke();
        } else {
          if (particlesRef.current.length > 0) {
            particlesRef.current = [];
          }
          if (ripplesRef.current.length > 0) {
            ripplesRef.current = [];
          }
        }
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [primaryColor]);

  return (
    <>
      {/* 
        Full-viewport Symbiote Canvas mounted directly to document.body via Portal.
        Guarantees 100% pixel-perfect coordinates.
      */}
      {mounted &&
        typeof document !== 'undefined' &&
        createPortal(
          <canvas
            ref={canvasRef}
            width={typeof window !== 'undefined' ? window.innerWidth : 1920}
            height={typeof window !== 'undefined' ? window.innerHeight : 1080}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              pointerEvents: 'none',
              zIndex: 9998,
            }}
          />,
          document.body
        )}

      {/* Button with Butter-Smooth Magnetic Spring Physics */}
      <Button
        ref={buttonRef}
        component={Link}
        to={to}
        onClick={onClick}
        sx={{
          backgroundColor: 'transparent',
          color: textColor,
          border: `2px solid ${primaryColor}`,
          borderRadius: '25px',
          fontWeight: 550,
          fontSize: '1.08rem',
          padding: '5px 35px',
          textTransform: 'capitalize',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
          willChange: 'transform',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:after': {
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '0px',
            bottom: '0',
            left: '0',
            backgroundColor: primaryColor,
            transition: 'height 0.25s linear',
            transformOrigin: 'bottom',
            zIndex: -1,
          },
          '&:hover:after': {
            transform: 'scale(1)',
            height: '100%',
          },
          '&:hover': {
            color: '#ffffff',
            boxShadow: `0 0 25px ${primaryColor}`,
          },
          ...sx,
        }}
      >
        {children}
      </Button>
    </>
  );
};

export default VenomHireButton;
